/* eslint-disable max-lines */

import { type SupportedCurrencies } from 'api/@types/Currency';
import { type UseGoogleMapViewProps } from 'api/google-api/hooks/useGoogleMapView';
import { type UserRole } from 'api/users/types';
import { type DocumentTypeValue } from 'features/documents/types';
import { type ReactElement } from 'react';

export enum CountryCodeEnum {
  GB = 'GB',
  PT = 'PT',
  ES = 'ES',
  PL = 'PL',
  FR = 'FR',
}

export type CountryCode = keyof typeof CountryCodeEnum;

export interface LimitedCountryListItem {
  value: CountryCode;
  flag: string;
  displayText: ReactElement;
}

export const NONE_COUNTRY_CODE = 'NONE';

export const CharityVettingUrlType = {
  GET_STARTED: 'get-started',
  SEARCH: 'search',
  CONTACT_DETAILS: 'contact-details',
  MANUAL_CONTACT_DETAILS: 'manual-contact-details',
  LEGAL_REPRESENTATIVE: 'legal-representative',
  REGISTRATION_DETAILS: 'registration',
  CHARITY_NUMBER: 'charity-number',
  COMPANY_REGISTRATION: 'company-registration',
  COMPANY_NUMBER: 'company-number',
  AUTHORISED_FOOD_AID: 'authorised-food-aid',
  ORGANISATION_PURPOSE: 'organisation-purpose',
  ORGANISATION_SPECIFIC_TYPE: 'organisation-specific-type',
  DECREE_PUBLICATION_DATES: 'decree-publication-dates',
  ARRETE_PUBLICATION_DATE: 'arrete-publication-date',
  ORGANISATION_SITUATION: 'organisation-situation',
  ORGANISATION_OTHER_OPTION: 'organisation-other-option',
  AGREMENT_DATE: 'agrement-date',
  SIREN: 'siren',
  RNA: 'rna',
  FOOD_SAFETY_QUESTION_THREE: 'food-safety-question-three',
  FOOD_SAFETY_MANAGEMENT_PLAN_DECLARATION: 'food-safety-management-plan-declaration',
  HACCP_DECLARATION: 'haccp-declaration',
  NIF: 'nif',
  NIPC: 'nipc',
  FISCAL_BENEFITS: 'fiscal-benefits',
  CIF: 'cif',
  KRS: 'krs',
  NIP: 'nip',
  DONATION_CERTIFICATE: 'donation-certificate',
  DONATION_PREFERENCES: 'donation-preferences',
  RGSEAA_QUESTION: 'rgseaa-question',
  RGSEAA: 'rgseaa',
  DONATION_HAS_KITCHEN: 'preparation-kitchen',
  DONATION_CONSUMPTION_TYPE: 'consumption-type',
  DONATION_HACCP: 'haccp',
  DONATION_HACCP_CERTIFICATE: 'haccp-certificate',
  ACCEPTS_ALL_FOODS: 'accept-all-foods',
  ACCEPTS_ALL_NON_FOOD: 'accept-all-non-food',
  ACCEPTS_ALL_FOOD_AND_NON_FOOD: 'accept-all-food-and-non-food',
  FOOD_HYGIENE: 'food-hygiene',
  FOOD_HYGIENE_CERTIFICATE: 'food-hygiene-certificate',
  SUBMIT_FOOD_HYGIENE_CERTIFICATE: 'submit-food-hygiene-certificate',
  UNWANTED_FOOD: 'unwanted-food',
  PLI: 'public-liability-insurance',
  PLI_COVER: 'public-liability-insurance-cover',
  SOCIAL_ACCOUNTS: 'social-accounts',
  REVIEW_DETAILS: 'review-details',
  PUBLIC_BENEFIT: 'public-benefit',
  ASSOCIATION_REGISTRATION: 'association-registration',
  FOOD_AID_HABILITATION: 'food-aid-habilitation',
  FOOD_ESTABLISHMENT_DECLARATION: 'food-establishment-declaration',
  FOOD_SAFETY_TRAINING_CERTIFICATE: 'food-safety-training-certificate',
  LIABILITY_INSURANCE: 'liability-insurance',
} as const;

export type CharityVettingUrlTypeValues =
  (typeof CharityVettingUrlType)[keyof typeof CharityVettingUrlType];

export type LocalisedFlowProgress = {
  [K in CharityVettingUrlTypeValues]: boolean;
};

export type OmittedLocalisedFlowProgress = Omit<
  LocalisedFlowProgress,
  | 'contact-details'
  | 'manual-contact-details'
  | 'legal-representative'
  | 'rgseaa-question'
  | 'rgseaa'
  | 'nif'
  | 'nipc'
  | 'krs'
  | 'siren'
  | 'nip'
  | 'fiscal-benefits'
  | 'preparation-kitchen'
  | 'consumption-type'
  | 'haccp'
  | 'haccp-certificate'
  | 'donation-certificate'
  | 'cif'
  | 'food-hygiene-certificate'
  | 'food-hygiene'
  | 'submit-food-hygiene-certificate'
  | 'public-liability-insurance-cover'
  | 'public-liability-insurance'
  | 'food-safety-question-three'
  | 'food-safety-management-plan-declaration'
  | 'haccp-declaration'
  | 'accept-all-foods'
  | 'accept-all-non-food'
  | 'accept-all-food-and-non-food'
  | 'unwanted-food'
  | 'charity-number'
  | 'company-registration'
  | 'company-number'
  | 'public-benefit'
  | 'rna'
  | 'organisation-purpose'
  | 'organisation-specific-type'
  | 'association-registration'
  | 'food-aid-habilitation'
  | 'food-establishment-declaration'
  | 'food-safety-training-certificate'
  | 'liability-insurance'
>;

export interface UnwantedFoodType {
  label: string | ReactElement;
  code: string;
  active?: boolean;
}

export const getSupportEmail = (countryIsoCode?: CountryCode) => {
  switch (countryIsoCode) {
    case CountryCodeEnum.PT:
      return 'charitysupport@toogoodtogo.pt';
    case CountryCodeEnum.ES:
      return 'charitysupport@toogoodtogo.es';
    case CountryCodeEnum.PL:
      return 'charitysupport@toogoodtogo.pl';
    case CountryCodeEnum.FR:
      return 'charitysupport@toogoodtogo.fr';
    default:
      return 'charitysupport@toogoodtogo.co.uk';
  }
};

export type SocialMediaType = 'facebook' | 'instagram' | 'linkedin';

export type SocialMediaAccountLinks = {
  type: SocialMediaType;
  link: string;
};

export type PliType = {
  code: SupportedCurrencies;
  minorUnits: number;
  decimals: number;
  value: number | undefined;
};

export type ErrorType = {
  data: {
    errors: {
      code: string;
      message: string;
    }[];
  };
};

export interface SubmitUnwantedFoodType {
  label: string | ReactElement;
  active: boolean;
}

export enum CharityTypeEnum {
  // PT charity types
  VOLUNTEERS_ORG = 'VOLUNTEERS_ORG',
  PRIVATE = 'PRIVATE_SOCIAL_SOLIDARITY_ORG',
  FOUNDATIONS_AND_PUBLIC_UTILITY_REGISTERED_ORG = 'FOUNDATIONS_AND_PUBLIC_UTILITY_REGISTERED_ORG',
  NGO = 'NGO',
  // ES && PL charity types
  ASSOCIATION = 'ASSOCIATION',
  FOUNDATION = 'FOUNDATION',
  // PL charity type
  REPRESENTATIVE_OFFICE_OF_FOREIGN_FOUNDATION = 'REPRESENTATIVE_OFFICE_OF_FOREIGN_FOUNDATION',
  // UK charity types
  REGISTERED_CHARITY = 'REGISTERED_CHARITY',
  REGISTERED_SOCIETY = 'REGISTERED_SOCIETY',
  COMMUNITY_INTEREST_COMPANY = 'COMMUNITY_INTEREST_COMPANY',
  SOCIAL_ENTERPRISE = 'SOCIAL_ENTERPRISE',
  LIMITED_COMPANY_NON_PROFIT = 'LIMITED_COMPANY_NON_PROFIT',
  // FR charity types
  AUTHORISED_FOOD_AID_ORGANIZATION = 'AUTHORISED_FOOD_AID_ORGANIZATION',
  RECOGNISED_PUBLIC_UTILITY_ASSOCIATION = 'RECOGNISED_PUBLIC_UTILITY_ASSOCIATION',
  RECOGNISED_PUBLIC_INTEREST_ASSOCIATION = 'RECOGNISED_PUBLIC_INTEREST_ASSOCIATION',
}

export enum DonationPreferencesEnum {
  ONLY_FOOD = 'ONLY_FOOD',
  FOOD_AND_NON_FOOD = 'FOOD_AND_NON_FOOD',
  NON_FOOD = 'NON_FOOD',
}

export enum OrganisationPurposeEnum {
  GENERAL_INTEREST = 'GENERAL_INTEREST',
  OTHER = 'OTHER',
}

export enum OrganisationSpecificTypeEnum {
  ASSOCIATION_LOI_1901 = 'ASSOCIATION_LOI_1901',
  ASSOCIATION_FONDATION_UTILITE_PUBLIQUE = 'ASSOCIATION_FONDATION_UTILITE_PUBLIQUE',
  ASSOCIATION_MOSELLE_ALSACE = 'ASSOCIATION_MOSELLE_ALSACE',
  FONDATION_UNIVERSITAIRE_PARTENARIALE = 'FONDATION_UNIVERSITAIRE_PARTENARIALE',
  FONDATION_ENTREPRISE = 'FONDATION_ENTREPRISE',
  MUSEE_DE_FRANCE = 'MUSEE_DE_FRANCE',
  ORGANISMES_SANS_BUT_LUCRATIF = 'ORGANISMES_SANS_BUT_LUCRATIF',
  OTHER = 'OTHER',
}

export enum OrganisationOtherOptionEnum {
  CULTUELLE_ALSACE_MOSELLE = 'CULTUELLE_ALSACE_MOSELLE',
  ENSEIGNEMENT_SUPERIEUR_ARTISTIQUE = 'ENSEIGNEMENT_SUPERIEUR_ARTISTIQUE',
  ENSEIGNEMENT_SUPERIEUR_CONSULAIRE = 'ENSEIGNEMENT_SUPERIEUR_CONSULAIRE',
  SOCIETE_ORGANISME_AGREE_ARTICLE_4_58_882 = 'SOCIETE_ORGANISME_AGREE_ARTICLE_4_58_882',
  ORGANISME_PRESENTATION_OEUVRES_ART = 'ORGANISME_PRESENTATION_OEUVRES_ART',
  PROJET_THESE_MECENAT_DOCTORAT = 'PROJET_THESE_MECENAT_DOCTORAT',
  SOCIETE_ETAT_ACTIONNAIRE_EXPOSITIONS = 'SOCIETE_ETAT_ACTIONNAIRE_EXPOSITIONS',
  SOCIETE_NATIONALE_PROGRAMME_ARTICLE_44 = 'SOCIETE_NATIONALE_PROGRAMME_ARTICLE_44',
  SOCIETE_NATIONALE_PROGRAMME_III_ARTICLE_44 = 'SOCIETE_NATIONALE_PROGRAMME_III_ARTICLE_44',
  FONDATION_PATRIMOINE_UTILITE_PUBLIQUE = 'FONDATION_PATRIMOINE_UTILITE_PUBLIQUE',
  FONDS_DE_DOTATION = 'FONDS_DE_DOTATION',
  ORGANISME_AGREE_238_BIS_PME = 'ORGANISME_AGREE_238_BIS_PME',
  FEDERATION_ORGANISMES_238_BIS = 'FEDERATION_ORGANISMES_238_BIS',
  ORGANISMES_SAUVEGARDE_BIENS_CULTURELS = 'ORGANISMES_SAUVEGARDE_BIENS_CULTURELS',
  ORGANISME_ETABLI_UE_AUTRE_QUE_FRANCE = 'ORGANISME_ETABLI_UE_AUTRE_QUE_FRANCE',
}

/** Organisation "other" options that require the "Date de l'agrément" step */
export const ORGANISATION_OTHER_OPTIONS_REQUIRING_AGREMENT: OrganisationOtherOptionEnum[] = [
  OrganisationOtherOptionEnum.SOCIETE_ORGANISME_AGREE_ARTICLE_4_58_882,
  OrganisationOtherOptionEnum.FONDATION_PATRIMOINE_UTILITE_PUBLIQUE,
  OrganisationOtherOptionEnum.ORGANISME_AGREE_238_BIS_PME,
  OrganisationOtherOptionEnum.FEDERATION_ORGANISMES_238_BIS,
  OrganisationOtherOptionEnum.ORGANISME_ETABLI_UE_AUTRE_QUE_FRANCE,
];

export enum ConsumptionTypeEnum {
  HUMAN_CONSUMPTION = 'HUMAN_CONSUMPTION',
  ANIMAL_CONSUMPTION = 'ANIMAL_CONSUMPTION',
  HUMAN_AND_ANIMAL_CONSUMPTION = 'HUMAN_AND_ANIMAL_CONSUMPTION',
}

export type VettingDocumentUpload = {
  documentId: string;
  fileName: string;
};

export type CharityVettingFormValues = {
  charityOnboarding: {
    search?: string;
    charityName: string;
    email: string;
    address: string;
    extraAddressLine?: string;
    postalCode: string;
    city: string;
    phoneNumber: string;
    countryIsoCode: CountryCode | null;
    pointOfContactName: string;
    isLegalRepresentative?: boolean;
    organizationDescription: string;
    socialMediaAccountLinks: SocialMediaAccountLinks[];
    acceptsAllFood?: boolean;
    acceptsAllNonFood?: boolean;
    acceptsAllFoodAndNonFood?: boolean;
    unwantedFoodItems: UnwantedFoodType[] | [];
    additionalUnwantedFoodCategories: UnwantedFoodType[] | [];
    manualFormSelected: boolean;
    numberOfUnwantedFoodItems: number;
    gmAddressCoordinates?: UseGoogleMapViewProps['coords'];
    charityType: CharityTypeEnum | null;
    // UK
    charityRegistrationId: string;
    isCompany?: boolean;
    companyRegistrationNumber?: string;
    hasPli?: boolean;
    pliCover: PliType;
    isFsaRated?: boolean;
    hasHygieneCertificate?: boolean;
    // PT
    nif?: number | null;
    nipc?: number | null;
    fiscalBenefitsArticle?: boolean;
    donationPreferences?: DonationPreferencesEnum;
    preparationKitchen?: boolean;
    consumptionType?: ConsumptionTypeEnum;
    haccp?: boolean;
    //  ES
    cif?: number | null;
    donationCertificate?: boolean;
    hasRgseaa?: boolean;
    rgseaa?: number | null;
    // PL
    nip?: number | null;
    krs?: number | null;
    isPublicBenefitOrg?: boolean;
    // FR
    isAuthorisedFoodAid?: boolean;
    organisationPurpose?: OrganisationPurposeEnum;
    organisationSpecificType?: OrganisationSpecificTypeEnum;
    decreePublicationDate?: string;
    officialJournalPublicationDate?: string;
    arretePublicationDate?: string;
    organisationSituation?: string;
    organisationOtherOption?: OrganisationOtherOptionEnum;
    agrementDate?: string;
    rna?: number | null;
    siren?: number | null;
    staffTrainedInFoodHygiene?: boolean;
    hasFoodSafetyManagementPlan?: boolean;
    hasHaccpProcedures?: boolean;
    vettingDocuments?: Partial<Record<DocumentTypeValue, VettingDocumentUpload>>;
  };
  flowManagement: {
    flowProgress: {
      GB: OmittedLocalisedFlowProgress;
      PT: OmittedLocalisedFlowProgress;
      ES: OmittedLocalisedFlowProgress;
      PL: OmittedLocalisedFlowProgress;
      FR:
        | OmittedLocalisedFlowProgress
        | {
            'food-safety-question-three': boolean;
          }
        | {
            'association-registration': boolean;
          };
    };
  };
};

export type CharityVettingSubmitCharityDetailsRequest = {
  charityName: string;
  email: string;
  address: string;
  postalCode: string;
  city: string;
  phoneNumber: string;
  countryIsoCode: CountryCode;
  pointOfContactName: string;
  role: UserRole;
  charityRegistrationId: string;
  isCompany?: boolean;
  acceptsAllFood?: boolean;
  acceptsAllNonFood?: boolean;
  acceptsAllFoodAndNonFood?: boolean;
  companyRegistrationNumber?: string;
  organizationDescription: string;
  socialMediaAccountLinks: SocialMediaAccountLinks[];
  isFsaRated?: boolean;
  hasHygieneCertificate?: boolean;
  unwantedFoodCategories: SubmitUnwantedFoodType[];
  additionalUnwantedFoodCategories: SubmitUnwantedFoodType[];
  pli: PliType;
  charityType?: CharityTypeEnum | null;
  nif?: number | null;
  nipc?: number | null;
  fiscalBenefitsArticle?: boolean;
  donationPreferences?: DonationPreferencesEnum;
  preparationKitchen?: boolean;
  consumptionType?: ConsumptionTypeEnum;
  haccp?: boolean;
  cif?: number | null;
  donationCertificate?: boolean;
  rgseaa?: number | null;
  krs?: number | null;
  nip?: number | null;
  isPublicBenefitOrg?: boolean;
  isAuthorisedFoodAid?: boolean;
  organisationPurpose?: OrganisationPurposeEnum;
  organisationSpecificType?: OrganisationSpecificTypeEnum;
  decreePublicationDate?: string;
  officialJournalPublicationDate?: string;
  arretePublicationDate?: string;
  organisationSituation?: string;
  organisationOtherOption?: OrganisationOtherOptionEnum;
  agrementDate?: string;
  siren?: number | null;
  rna?: number | null;
  staffTrainedInFoodHygiene?: boolean;
};

export type CharityVettingSubmissionResponse = {
  data: CharityVettingSubmitCharityDetailsRequest;
};

// ---------------------------------------------------------------------------
// Stepped vetting API (start / role / patch-per-step)
// See docs spec: 2026-07-02-stepped-charity-vetting-partial-update-design.
// ---------------------------------------------------------------------------

export const CompletionStatus = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
} as const;

export type CompletionStatusType = (typeof CompletionStatus)[keyof typeof CompletionStatus];

/** Step 1 — create the charity with the minimum values (+ optional contact fields). */
export type StartCharityVettingRequest = {
  charityName: string;
  email: string;
  countryIsoCode: CountryCode;
  address?: string;
  postalCode?: string;
  city?: string;
  phoneNumber?: string;
  pointOfContactName?: string;
};

/** Role step — grant/change the signed-in user's charity role. Re-callable. */
export type SetVettingRoleRequest = {
  role: UserRole;
};

/**
 * Partial update — any subset of the vetting field set, applied in addition mode
 * (present + non-null + non-blank only; nothing is cleared). Field names follow
 * the API spec, which differs from the form/legacy names in a few places (food
 * safety booleans, `pli`, unwanted-food categories, string-typed registry ids).
 */
export type UpdateCharityVettingRequest = {
  // Identity (mirrored onto the Charity entity)
  charityName?: string;
  email?: string;
  countryIsoCode?: CountryCode;
  charityRegistrationId?: string;
  address?: string;
  extraAddressLine?: string;
  postalCode?: string;
  city?: string;
  // Contact
  phoneNumber?: string;
  pointOfContactName?: string;
  // Organisation
  isCompany?: boolean;
  companyRegistrationNumber?: string;
  organizationDescription?: string;
  charityType?: CharityTypeEnum;
  socialMediaAccountLinks?: SocialMediaAccountLinks[];
  fileUploadLinks?: string[];
  // Food safety / hygiene
  isFsaRated?: boolean;
  fsaRating?: number;
  hasHygieneCertificate?: boolean;
  hygieneCertificateLink?: string;
  haccp?: boolean;
  preparationKitchen?: boolean;
  isFoodTrained?: boolean;
  isFoodBusiness?: boolean;
  // Food acceptance / donations
  unwantedFoodCategories?: SubmitUnwantedFoodType[];
  additionalUnwantedFoodCategories?: SubmitUnwantedFoodType[];
  acceptsAllFood?: boolean;
  acceptsAllNonFood?: boolean;
  acceptsAllFoodAndNonFood?: boolean;
  donationPreferences?: DonationPreferencesEnum;
  consumptionType?: ConsumptionTypeEnum;
  pli?: PliType;
  // Fiscal / registry (country-specific) — strings per the spec
  nif?: string;
  nipc?: string;
  fiscalBenefitsArticle?: boolean;
  cif?: string;
  donationCertificate?: boolean;
  rgseaa?: string;
  krs?: string;
  nip?: string;
  siren?: string;
  rna?: string;
  isPublicBenefitOrg?: boolean;
  // FR-only
  organisationPurpose?: OrganisationPurposeEnum;
  organisationSpecificType?: OrganisationSpecificTypeEnum;
  organisationOtherOption?: OrganisationOtherOptionEnum;
  decreePublicationDate?: string;
  officialJournalPublicationDate?: string;
  arretePublicationDate?: string;
  agrementDate?: string;
  organisationSituation?: string;
  isAuthorisedFoodAid?: boolean;
  // Wizard completion
  completionStatus?: CompletionStatusType;
};

/** The vetting request view returned by every stepped endpoint. */
export type VettingRequestData = Partial<UpdateCharityVettingRequest> & {
  charityName: string;
  email: string;
  completionStatus: CompletionStatusType;
};

export type VettingApiError = {
  code: string;
  field?: string | null;
  message?: string | null;
};

export type VettingResponse = {
  data: VettingRequestData;
  errors?: VettingApiError[];
};
